import { STATUSES } from 'constants/InterventionConstants';
import * as types from 'modules/interventions/actionTypes';
import { closeInterventionSocketMatcher, deleteInterventionSocketMatcher } from 'modules/interventions/reducers/utils';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

/**
 * @typedef {Record<import('modules/interventions/types/interfaces/kanban').KanbanEnum, Array<string>>} KanbanIdsByGroupReducer
 */

/** @type {KanbanIdsByGroupReducer} */
export const initialState = {
  CHECK_IN_COMPLETED: [],
  CHECK_OUT_COMPLETED: [],
  CHECK_OUT_READY: [],
  DEFAULT: [],
  HOME_CHECK_IN_COMPLETED: [],
  IN_HOUSE: [],
};

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {string} interventionId
 * @returns {KanbanIdsByGroupReducer}
 */
const removeInterventionId = (state, interventionId) =>
  Object.entries(state).reduce(
    (acc, [group, ids]) => ({
      ...acc,
      [group]: ids.filter(id => id !== interventionId),
    }),
    {}
  );

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {import('modules/interventions/types/actions').FetchKanbanInterventionsSuccessAction} action
 * @returns {KanbanIdsByGroupReducer}
 */
const afterFetchKanban = (_, { payload }) =>
  Object.entries(payload).reduce(
    (acc, [field, interventions]) => ({
      ...acc,
      [field]: interventions.map(({ id }) => id),
    }),
    initialState
  );

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {import('modules/interventions/types/actions').FetchKanbanInterventionsSuccessAction} action
 * @returns {KanbanIdsByGroupReducer}
 */
const afterRefreshFromSocket = (state, { payload, meta: { ids: allNewIds = [] } = {} }) => {
  const clearState = allNewIds.reduce(removeInterventionId, state);

  return Object.entries(payload).reduce(
    (acc, [field, interventions]) => ({
      ...acc,
      [field]: [...acc[field], ...interventions.map(({ id }) => id)],
    }),
    clearState
  );
};

const reset = () => initialState;

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {import('modules/interventions/types/actions').MoveKanbanInterventionAction} action
 * @returns {KanbanIdsByGroupReducer}
 */
const onMoveOnKanban = (state, { payload }) => ({
  ...state,
  [payload.from]: state[payload.from].filter(id => id !== payload.interventionId),
  [payload.to]: uniq([...state[payload.to], payload.interventionId]),
});

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {import('modules/interventions/types/actions').MoveKanbanInterventionFailureAction} action
 * @returns {KanbanIdsByGroupReducer}
 */
const rollbackDragAndDrop = (state, { payload: { rollback } }) =>
  onMoveOnKanban(state, {
    payload: { interventionId: rollback.interventionId, from: rollback.to, to: rollback.from },
  });

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {import('modules/global/generic.types').Action<import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention>} action
 * @returns {KanbanIdsByGroupReducer}
 */
const afterInterventionStatusChange = (state, { payload: { status, id: interventionId } }) => {
  const isDeleteOrClose = status === STATUSES.CLOSED || status === STATUSES.DELETED;
  if (isDeleteOrClose) {
    return Object.entries(state).reduce(
      (acc, [group, ids]) => ({
        ...acc,
        [group]: ids.filter(id => id !== interventionId),
      }),
      {}
    );
  }
  return state;
};

/**
 * @param {KanbanIdsByGroupReducer} state
 * @param {import('modules/global/generic.types').Action<{interventionId: string}>} action
 * @returns {KanbanIdsByGroupReducer}
 */
const afterDeleteIntervention = (state, { payload: { interventionId, entityId } }) =>
  removeInterventionId(state, interventionId ?? entityId);

export default createReducer(
  initialState,
  {
    [LOCATION_CHANGE]: reset,
    [types.FETCH_KANBAN_INTERVENTIONS_FAILURE]: reset,
    [types.MOVE_KANBAN_INTERVENTION]: onMoveOnKanban,
    [types.MOVE_KANBAN_INTERVENTION_FAILURE]: rollbackDragAndDrop,
    // Status update
    [types.DELETE_INTERVENTION_SUCCESS]: afterDeleteIntervention,
    [types.UPDATE_INTERVENTION_STATUS_SUCCESS]: afterInterventionStatusChange,
  },
  [
    {
      // Normal case, we received the full board, so we replace the state
      matcher: ({ type, meta }) => type === types.FETCH_KANBAN_INTERVENTIONS_SUCCESS && !meta?.ids,
      reducer: afterFetchKanban,
    },
    {
      // When there is `meta.ids` it means it's coming from the websocket
      // So we *update* the state instead of replacing it
      matcher: ({ type, meta }) => type === types.FETCH_KANBAN_INTERVENTIONS_SUCCESS && Array.isArray(meta?.ids),
      reducer: afterRefreshFromSocket,
    },
    // Kanban (websocket)
    {
      matcher: closeInterventionSocketMatcher,
      reducer: afterDeleteIntervention
    },
    {
      matcher: deleteInterventionSocketMatcher,
      reducer: afterDeleteIntervention
    },
  ]
);
